import {
  FreeLayoutPluginContext, FlowNodeEntity, WorkflowPortEntity, WorkflowNodeLinesData, delay,
  WorkflowLineEntity,
  // WorkflowEdgeJSON,
  // WorkflowJSON,
} from '@flowgram.ai/free-layout-editor';
import { getAllExecuteIdMap, getMcpTaskingMap } from '$/store/useFlowStore';
import { getOutputPortId } from './getProtType';

type LineRebuildToPortItem = {
  toNode: FlowNodeEntity;
  toPort: WorkflowPortEntity;
};

export const lineReBuild = async (ctx: FreeLayoutPluginContext, currentNode: FlowNodeEntity, firstChild?: FlowNodeEntity) => {
  if (!firstChild) return;
  const nodeLines = currentNode.getData(WorkflowNodeLinesData);
  const toNodePortList: { toNode: FlowNodeEntity | undefined; toPort: WorkflowPortEntity | undefined; }[] = [];
  const lineList: WorkflowLineEntity[] = [];
  nodeLines.outputLines.forEach((line) => {
    toNodePortList.push({
      toNode: line.to,
      toPort: line.toPort,
    });
    lineList.push(line);
  });

  await delay(50);

  // const portsData = firstChild.getData(WorkflowNodePortsData);

  // console.log('portsData', portsData);
  // const newLineList: WorkflowEdgeJSON[] = [];
  toNodePortList.forEach((item) => {
    if (item.toNode) {
      // newLineList.push({
      //   sourceNodeID: firstChild.id,
      //   sourcePortID: portsData?.outputPorts?.[0]?.portID,
      //   targetNodeID: item.toNode.id,
      //   targetPortID: item.toPort!.portID,
      // });
      ctx.document.linesManager.createLine({
        from: firstChild.id,
        fromPort: getOutputPortId(firstChild.flowNodeType.toString(), firstChild.id),
        to: item.toNode.id,
        toPort: item.toPort!.portID,
      });
    }
  });
  lineList.forEach((line) => {
    line.dispose();
  });
};

export const lineReBuildWithChildren = async (ctx: FreeLayoutPluginContext, currentNode: FlowNodeEntity) => {
  const allExecuteIdMap = getAllExecuteIdMap();
  if (!Object.keys(allExecuteIdMap).includes(currentNode.id)) return;
  await delay(50);
  const childrenNodeIdList = currentNode.toJSON()?.data?.childrenNodeList || [];
  const childrenNodeList = childrenNodeIdList.map((id: string) => ctx.document.getNode(id));
  const mcpTaskingMap = getMcpTaskingMap();
  const mcpTaskingIdList = Object.keys(mcpTaskingMap);
  const lineList: WorkflowLineEntity[] = [];
  const toNodePortList: { toNode: FlowNodeEntity | undefined; toPort: WorkflowPortEntity | undefined; }[] = [];
  // const slicedChildrenNodeList = childrenNodeList.slice(1);
  childrenNodeList.forEach((childNode: FlowNodeEntity) => {
    if (!childNode) return;
    const nodeLines = childNode.getData(WorkflowNodeLinesData);
    nodeLines.outputLines.forEach((line: WorkflowLineEntity) => {
      if (line.to?.id && mcpTaskingIdList.includes(line.to.id)) {
        lineList.push(line);
        toNodePortList.push({
          toNode: line.to,
          toPort: line.toPort,
        });
      }
    });
  });

  const [firstChild] = childrenNodeList;

  // const portsData = firstChild.getData(WorkflowNodePortsData);

  toNodePortList.forEach((item) => {
    if (item.toNode) {
      ctx.document.linesManager.createLine({
        from: firstChild.id,
        fromPort: getOutputPortId(firstChild.flowNodeType.toString(), firstChild.id),
        to: item.toNode.id,
        toPort: item.toPort!.portID,
      });
    }
  });

  lineList.forEach((line) => {
    line.dispose();
  });
};

/**
 * @description 批量生成子节点后：子节点列表里凡有「连向列表外」的出线，改由第一个子节点统一连到原下游。
 *   顺序：先根据收集到的目标 `createLine`（新线就绪），再 `dispose` 旧线，避免短暂断连。
 * @param ctx - 画布上下文
 * @param currentNode - 父节点（读取 `data.childrenNodeList`）
 * @param firstChild - 作为新出线起点的子节点，缺省取 `childrenNodeList[0]`
 */
export const lineReBuildWithChildrenForNew = async (
  ctx: FreeLayoutPluginContext,
  currentNode: FlowNodeEntity,
  firstChild?: FlowNodeEntity,
) => {
  await delay(50);
  const childrenNodeIdList: string[] = currentNode.toJSON()?.data?.childrenNodeList || [];
  if (!childrenNodeIdList.length) {
    return;
  }

  // const childrenIdSet = new Set(childrenNodeIdList);
  const childrenNodeList = childrenNodeIdList
    .map((id: string) => ctx.document.getNode(id))
    .filter((n): n is FlowNodeEntity => Boolean(n));

  const sourceChild = firstChild ?? childrenNodeList[0];
  if (!sourceChild) {
    return;
  }

  const beforeLastNode = childrenNodeList?.[1];
  const linesToDispose: WorkflowLineEntity[] = [];
  const toNodePortList: LineRebuildToPortItem[] = [];
  const seenTargetKey = new Set<string>();
  if (beforeLastNode) {
    const beforeLastNodeLines = beforeLastNode.getData(WorkflowNodeLinesData);
    beforeLastNodeLines.outputLines.forEach((line: WorkflowLineEntity) => {
      const { to: toNode, toPort } = line;
      if (!toNode || !toPort) {
        return;
      }
      const dedupeKey = `${toNode.id}:${toPort.portID}`;
      if (seenTargetKey.has(dedupeKey)) {
        return;
      }
      seenTargetKey.add(dedupeKey);
      linesToDispose.push(line);
      toNodePortList.push({ toNode, toPort });
    });
  }
  /** 旧线实体，最后统一断开 */
  /** 去重后的 (目标节点, 目标端口)，避免多子节点同指一处时重复建线 */

  // childrenNodeList.forEach((childNode) => {
  //   const nodeLines = childNode.getData(WorkflowNodeLinesData);
  //   nodeLines.outputLines.forEach((line: WorkflowLineEntity) => {
  //     const { to: toNode, toPort } = line;
  //     if (!toNode || !toPort) {
  //       return;
  //     }
  //     // 仅处理「向后的线」：连到 children 列表之外的下游；子节点之间的连线保留不动
  //     if (childrenIdSet.has(toNode.id)) {
  //       return;
  //     }
  //     const dedupeKey = `${toNode.id}:${toPort.portID}`;
  //     if (seenTargetKey.has(dedupeKey)) {
  //       return;
  //     }
  //     seenTargetKey.add(dedupeKey);
  //     linesToDispose.push(line);
  //     toNodePortList.push({ toNode, toPort });
  //   });
  // });

  if (!toNodePortList.length) {
    return;
  }

  const fromPort = getOutputPortId(sourceChild.flowNodeType.toString(), sourceChild.id);
  toNodePortList.forEach((item) => {
    ctx.document.linesManager.createLine({
      from: sourceChild.id,
      fromPort,
      to: item.toNode.id,
      toPort: item.toPort.portID,
    });
  });

  linesToDispose.forEach((line) => {
    line.dispose();
  });
};
